import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, Checkbox, FormControlLabel, IconButton, Stack,
  useMediaQuery, useTheme,
} from "@mui/material";
import { ArrowBack, ArrowForward, Close, OndemandVideo, MenuBook } from "@mui/icons-material";
import { useLanguage } from "../../contexts/LanguageContext";
import { PublicCourseCard } from "../../services/coursesService";
import { localizedTitle } from "../../utils/localizedContent";
import { VIDEO_ADDON_PER_COURSE, basePriceOf, computePlanPrice } from "../../utils/planPricing";
import { MEMBERSHIP_COLORS } from "./MembershipStyles";

/** The single-leaflet plan a course is unlocked with from the Classes page. */
export const UNLOCK_PLAN_ID = "starter";
export const UNLOCK_PLAN_NAME = "Individual Leaflet";

interface Props {
  open: boolean;
  course: PublicCourseCard | null;
  onClose: () => void;
  /** `withVideo` is whether the companion video add-on was ticked. */
  onConfirm: (withVideo: boolean) => void;
}

const BLUE  = MEMBERSHIP_COLORS.blue;
const NAVY  = MEMBERSHIP_COLORS.navy;
const MUTED = MEMBERSHIP_COLORS.muted;

const TITLE_ID = "unlock-course-dialog-title";
const DESC_ID  = "unlock-course-dialog-description";

/**
 * Confirms a single-leaflet unlock and offers the companion video with it.
 *
 * Only opened for a course that carries a companion video — with nothing to
 * choose, the Classes page goes straight to checkout instead.
 */
const UnlockCourseDialog: React.FC<Props> = ({ open, course, onClose, onConfirm }) => {
  const theme = useTheme();
  const { t, isRTL, language } = useLanguage();
  const td = t.classes.unlockDialog;
  const ForwardIcon = isRTL ? ArrowBack : ArrowForward;
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [withVideo, setWithVideo] = useState(false);

  // Each opening starts without the add-on, whichever course it is for.
  useEffect(() => {
    if (open) setWithVideo(false);
  }, [open]);

  if (!course) return null;

  const basePrice = basePriceOf(UNLOCK_PLAN_ID);
  const total = computePlanPrice({
    planId: UNLOCK_PLAN_ID,
    videoCourseIds: withVideo ? [course._id] : [],
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESC_ID}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: "24px" }, overflow: "hidden" } }}
    >
      {/* ── Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #0D2137 100%)`,
        px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 },
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={TITLE_ID}
              component="h2"
              fontWeight={800}
              sx={{ color: "#fff", letterSpacing: "-.02em", fontSize: { xs: "1.15rem", sm: "1.35rem" }, lineHeight: 1.25 }}
            >
              {td.title}
            </Typography>
            <Typography
              id={DESC_ID}
              variant="body2"
              sx={{ color: "rgba(255,255,255,.6)", mt: .5, fontSize: { xs: ".8rem", sm: ".85rem" } }}
            >
              {td.subtitle}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label={t.common.close}
            sx={{ color: "rgba(255,255,255,.5)", flexShrink: 0, mt: -.5, mr: -1 }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Course */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
          <Box sx={{
            width: 72, height: 54, flexShrink: 0, borderRadius: "12px",
            background: course.thumbnail
              ? `url(${course.thumbnail}) center/cover no-repeat`
              : `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
          }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>
              {localizedTitle(course, language)}
            </Typography>
            <Typography sx={{ color: MUTED, fontSize: ".8rem" }}>
              {t.classes.lessonsCount(course.itemCount)} · {t.classes.videosCount(course.videoCount)}
            </Typography>
          </Box>
        </Stack>

        {/* Line items */}
        <Box sx={{ border: `1px solid ${MEMBERSHIP_COLORS.border}`, borderRadius: "16px", overflow: "hidden" }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
            px: 2, py: 1.5,
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <MenuBook sx={{ color: BLUE, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: ".9rem" }}>
                {td.leaflet}
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 800, color: NAVY, flexShrink: 0 }}>${basePrice}</Typography>
          </Box>

          <Box
            onClick={() => setWithVideo(v => !v)}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              px: 2, py: 1, cursor: "pointer",
              borderTop: `1px solid ${MEMBERSHIP_COLORS.border}`,
              bgcolor: withVideo ? "rgba(21,101,192,.06)" : "transparent",
              transition: "background .2s ease",
            }}
          >
            <FormControlLabel
              sx={{ m: 0, minWidth: 0 }}
              onClick={e => e.stopPropagation()}
              control={
                <Checkbox
                  size="small"
                  checked={withVideo}
                  onChange={() => setWithVideo(v => !v)}
                  sx={{ py: .5, color: "#CBD5E1", "&.Mui-checked": { color: BLUE } }}
                />
              }
              label={
                <Stack direction="row" spacing={.8} alignItems="center">
                  <OndemandVideo sx={{ color: withVideo ? BLUE : MUTED, fontSize: 18 }} />
                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: NAVY }}>
                    {td.withVideo}
                  </Typography>
                </Stack>
              }
            />
            <Typography sx={{ fontWeight: 800, fontSize: ".85rem", color: withVideo ? BLUE : MUTED, flexShrink: 0 }}>
              {td.withVideoHint(VIDEO_ADDON_PER_COURSE)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        display: "flex",
        flexDirection: { xs: "column-reverse", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: 1.5,
        px: { xs: 2, sm: 3 }, py: 2,
        pb: { xs: "calc(16px + env(safe-area-inset-bottom))", sm: 2 },
        borderTop: `1px solid ${MEMBERSHIP_COLORS.border}`,
        "& > :not(style) ~ :not(style)": { ml: 0 },
      }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: MUTED }}>{td.total}</Typography>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: NAVY, lineHeight: 1.1 }}>
            ${total}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => onConfirm(withVideo)}
          endIcon={<ForwardIcon />}
          sx={{
            borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3, py: 1.1,
            bgcolor: BLUE, boxShadow: "0 4px 14px rgba(21,101,192,0.3)",
            "&:hover": { bgcolor: NAVY },
          }}
        >
          {td.continueToPayment}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnlockCourseDialog;
